import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Website} from '../models';
import {environment} from '~environments/environment';
import {AuthenticatedUser, Club, Module, Webhook} from '../models';
import {BasicStatusTranslationService, ClubStatusTranslationsService, ProductSettingsTranslationService} from 'src/app/shared/translations';

/**
 * CRUD service for the club-overview entity.
 */
@Injectable()
export class ClubService {
  public API_URL: any = `${environment.envAPI}`;
  public API_URL_CLUBS: any = `${environment.envAPI}/clubs`;
  public API_URL_USERS: any = `${environment.envAPI}/users`;
  public API_MEDIA_OBJECTS: any = `${environment.envAPI}/logos`;
  public API_URL_MODULES: any = `${environment.envAPI}/modules`;
  public API_URL_WEBHOOKS: any = `${environment.envAPI}/tixx-connect-webhooks`;

  public currentListOfClubs: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castListOfClubs: any = this.currentListOfClubs.asObservable();

  constructor(
    private http: HttpClient,
    private clubStatuses: ClubStatusTranslationsService,
    private statusTranslations: BasicStatusTranslationService,
    private productTranslations: ProductSettingsTranslationService
  ) {
  }

  /**
   * Get club-overview by id.
   */
  public getClubById(id: number): Observable<Club> {
    return this.http.get<Club>(`${this.API_URL_CLUBS}/${id}`).pipe(
      map(club => new Club(club, this.clubStatuses))
    );
  }

  /**
   * Get all clubs.
   */
  public fetchAuthenticatedUserClubs(currentPage: number = 1): Observable<any> {
    const API = `${this.API_URL}/me/clubs`;
    const params = new HttpParams()
      .set('order[status]', 'asc')
      .set('order[name]', 'asc')
      .set('page', currentPage.toString())
      .set('itemsPerPage', '12')
      .set('status[]', 'active')
      .append('status[]', 'inactive');

    return this.getClubs(API, params);
  }

  /**
   * Get all enabled clubs.
   */
  public fetchEnabledClubs(user: AuthenticatedUser): Observable<any> {
    const API = user.isAdmin ? `${this.API_URL_CLUBS}` : `${this.API_URL_USERS}/${user.id}/clubs`;
    const params = new HttpParams()
      .set('order[status]', 'asc')
      .set('order[name]', 'asc')
      .set('status', 'active')
      .set('itemsPerPage', '500');

    return this.getClubs(API, params);
  }

  /**
   * Get all enabled clubs.
   */
  public fetchAllClubs(user: AuthenticatedUser): Observable<any> {
    const API = user.isAdmin ? `${this.API_URL_CLUBS}` : `${this.API_URL_USERS}/${user.id}/clubs`;
    const params = new HttpParams()
      .set('order[status]', 'asc')
      .set('order[name]', 'asc')
      .set('itemsPerPage', '500');

    return this.getClubs(API, params);
  }

  /**
   * Search customers by a provided query.
   */
  public searchClubs(user: AuthenticatedUser, query: string, currentPage: number = 1): Observable<any> {
    const API = user.isAdmin ? `${this.API_URL_CLUBS}` : `${this.API_URL_USERS}/${user.id}/clubs`;
    const params = new HttpParams()
      .set('order[status]', 'asc')
      .set('order[name]', 'asc')
      .set('page', currentPage.toString())
      .set('itemsPerPage', '12')
      .set('status[]', 'active')
      .append('status[]', 'inactive')
      .set('name', query);

    return this.getClubs(API, params);
  }

  private getClubs(API: string, params: HttpParams): Observable<any> {
    return this.http.get<Array<Club>>(`${API}`, {params}).pipe(
      map((response: any) => {
        const clubs: Array<Club> = [];
        response['hydra:member'].forEach((club: any) => {
          clubs.push(new Club(club, this.clubStatuses));
        });
        return {
          clubs,
          totalItems: response['hydra:totalItems']
        };
      })
    );
  }

  /**
   * Delete club-overview.
   */
  public deleteClub(clubId: number): Observable<any> {
    return this.http.delete<Club>(`${this.API_URL_CLUBS}/${clubId}`);
  }

  /**
   * Create a new club-overview.
   */
  public createNewClub(club: Club): Observable<Club> {
    return this.http.post<Club>(`${this.API_URL_CLUBS}`, club);
  }

  /**
   * Create a new club-overview.
   */
  public createWebhook(webhook: Webhook): Observable<Webhook> {
    return this.http.post<Webhook>(`${this.API_URL_WEBHOOKS}`, webhook);
  }

  /**
   * Update a club-overview.
   */
  public updateClub(club: Club): Observable<Club> {
    return this.http.patch<Club>(`${this.API_URL_CLUBS}/${club.id}`, club);
  }

  /**
   * Update a club-overview.
   */
  public updateModules(module: Module): Observable<Module> {
    return this.http.patch<Module>(`${this.API_URL_MODULES}/${module.id}`, module);
  }

  /**
   * Update a club-overview.
   */
  public updateWebhooks(webhook: Webhook): Observable<Webhook> {
    return this.http.patch<Webhook>(`${this.API_URL_WEBHOOKS}/${webhook.id}`, webhook);
  }

  /**
   * Update a club-overview.
   */
  public updateWebsite(website: Website): Observable<Website> {
    return this.http.patch<Website>(`${this.API_URL}/l/websites/${website.id}`, website);
  }

  /**
   * Upload a logo.
   */
  public uploadLogo(logo: FormData): Observable<any> {
    return this.http.post<any>(`${this.API_MEDIA_OBJECTS}`, logo);
  }

  /**
   * Retrieve logo path / name.
   */
  public getPathToImageByMediaObject(mediaObjectIri: string): Observable<any> {
    const thirdItem: number = 3;
    const mediaObjectId = mediaObjectIri.split('/')[thirdItem];
    return this.http.get<any>(`${this.API_MEDIA_OBJECTS}/${mediaObjectId}`);
  }

  /**
   * Set new list of clubs
   */
  public setListOfClubs(listOfClubs: any): void {
    this.currentListOfClubs.next(listOfClubs);
    this.sortClubsByName(this.currentListOfClubs.getValue());
    this.sortClubsByStatus(this.currentListOfClubs.getValue());
  }

  /**
   * Sorts the list of clubs by active first
   */
  public sortClubsByStatus(clubs: Array<Club>): void {
    clubs.sort((x, y) => (x.status === y.status) ? 0 : x.status.localeCompare(y.status));
  }

  /**
   * Sorts the list of clubs by name
   */
  public sortClubsByName(clubs: Array<Club>): void {
    clubs.sort((x, y) => x.name.localeCompare(y.name));
  }

  /**
   * Retrieves the modules for the specific club-overview
   */
  public getModules(clubId: number): Observable<any> {
    return this.http.get<any>(`${this.API_URL_CLUBS}/${clubId}/modules`).pipe(
      map(response => response['hydra:member'].map(module => new Module(module, this.statusTranslations, this.productTranslations)))
    );
  }

  /**
   * Retrieves the webhooks for the specific club-overview
   */
  public getWebhooks(clubId: number): Observable<any> {
    return this.http.get<any>(`${this.API_URL_CLUBS}/${clubId}/webhooks`);
  }

  /**
   * Retrieves the website settings for the specific club-overview
   */
  public getWebsite(clubId: number): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/l/organizers/${clubId}/website`).pipe(
      map(response => new Website(response))
    );
  }
}
